import React, { memo, ChangeEvent } from 'react'
import { BottomDrawer } from 'components/layout/BottomDrawer'
import { Box } from 'transfix-ui/core'
import { useFeatureFlag, useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { IFile } from 'apps/fleet-planner/types'
import {
  CTA_STATUS_UPDATE_STATES,
  CTA_UPLOAD_POD_STATES,
  IGetNextDriverStatus,
} from 'apps/fleet-planner/utils/driverStatuses'
import { TrackingIcon } from 'transfix-icons'
import { Button } from 'transfix-ui'
import { AddAttachment } from '../form-components/AddAttachment'

export interface IStatusUpdateDrawerProps {
  nextStatusData: IGetNextDriverStatus
  onCtaClick: () => void
  onSendLocationClick: () => void
  onPodUpload: (event: ChangeEvent<HTMLInputElement>) => void
  loadStatus: LOAD_STATE
  showLocationLoading: boolean
  isOpen: boolean
  pods: IFile[]
  isExternal: boolean
}

export const StatusUpdateDrawer = memo<IStatusUpdateDrawerProps>(
  ({
    nextStatusData,
    onCtaClick,
    onSendLocationClick,
    loadStatus,
    showLocationLoading,
    onPodUpload,
    pods = [],
    isExternal,
  }) => {
    const { getFlag } = useFeatureFlagContext()
    const { enabled: isFpCarrierPortalEnabled } = getFlag('fpCarrierPortal')
    const { CtaHeader = '', CtaLabel = '' } = nextStatusData || {}
    const ctaStatusUpdates = isFpCarrierPortalEnabled
      ? [...CTA_STATUS_UPDATE_STATES, LOAD_STATE['Ready for Dispatch']]
      : CTA_STATUS_UPDATE_STATES
    const isOpen =
      ctaStatusUpdates.includes(loadStatus) ||
      (CTA_UPLOAD_POD_STATES.includes(loadStatus) && pods?.length === 0)

    const showSendLocationButton = useFeatureFlag('driverLedLocation') && isExternal

    const locationButtonIcon = showLocationLoading ? null : <TrackingIcon size='large' />

    const prefixedCtaLabel = CtaLabel === 'Dispatched' ? 'Mark as Dispatched' : CtaLabel

    return (
      <BottomDrawer isOpen={isOpen}>
        <div className='body-medium tc mb5 mt2'>{CtaHeader}</div>
        {ctaStatusUpdates.includes(loadStatus) && (
          <Button
            color='primary'
            fullWidth
            size='large'
            onClick={onCtaClick}
            data-test='cta-button'>
            {prefixedCtaLabel}
          </Button>
        )}
        {CTA_UPLOAD_POD_STATES.includes(loadStatus) && (
          <AddAttachment onChange={onPodUpload} text='Upload POD' />
        )}
        {showSendLocationButton && (
          <Box mt={1}>
            <Button
              color='secondary'
              fullWidth
              size='large'
              onClick={onSendLocationClick}
              disabled={showLocationLoading}
              startIcon={locationButtonIcon}>
              <span>
                {showLocationLoading ? 'Waiting to Send Location...' : 'Share Current Location'}
              </span>
            </Button>
          </Box>
        )}
        <div className='body-semibold text-secondary tc mt5'>Your dispatcher will be notified</div>
      </BottomDrawer>
    )
  }
)
